<script>
  export let fullscreen = false;
  export let collapsibleMenu = false;
  export let collapsibleMenuLabel = "Filter";
  export let expanded = false;
  export let fitScreen = false;
  export let splitScreen = false;
  export let fitContent = false;
  export let shrinkContent = false;
  export let leftOverlayOffset = "0rem";
  export let wrapperClass = "";
  export let canvasClass = "";
  export let embedded = false;
  export let seamless = false;
  export let seamlessPadding = false;
  export let emptyState = false;
  export let loading = false;
  export let loadingTitle = "Loading...";
  export let loadingHint = "";

  import CollapsibleMenu from "../CollapsibleMenu/CollapsibleMenu.svelte";
  import SectionLoadingState from "./SectionLoadingState.svelte";
</script>

<div
  class:embedded_wrapper={embedded}
  class:seamless
  class:empty_state={emptyState && !loading}
  class:seamless_with_menu={seamless && fullscreen === false}
  class:seamless_padding={seamlessPadding}
  class={`collapsible_wrapper ${wrapperClass}`}
>
  {#if collapsibleMenu === true}
    <CollapsibleMenu {expanded} label={collapsibleMenuLabel}>
      <slot name="collapsibleMenu" />
    </CollapsibleMenu>
  {/if}

  <div 
    class:view_wrapper={true}
    class:view_wrapper_expanded_menu={expanded}
    style={`--left-overlay-offset: ${leftOverlayOffset};`}>
    <div
      class={`bx--section_wrapper-canvas ${canvasClass}`}
      class:fullscreen_canvas={fullscreen === false}
      class:fit_screen={fitScreen === true}
      class:splitScreenLeft={splitScreen === true}
      class:fit_content={fitContent === true}
      class:shrink_content={shrinkContent === true}
      class:loading_canvas={loading === true}
    >
      {#if loading}
        <SectionLoadingState title={loadingTitle} hint={loadingHint} />
      {:else if emptyState}
        <div class="empty_state_content">
          <div class="empty_state_icon" aria-hidden="true">
            {#if $$slots.emptyStateIcon}
              <slot name="emptyStateIcon" />
            {:else}
              <img src="/img/assistant/icons/info.svg" alt="" />
            {/if}
          </div>
          <div class="empty_state_text">
            <slot name="emptyState" />
          </div>
        </div>
      {:else}
        <slot name="main" />
      {/if}
    </div>
    {#if $$slots.sidePanel}
      <div class="side_panel">
        <slot name="sidePanel" />
      </div>
    {/if}
    {#if fullscreen === false}
      <div
        class="side_menu"
        class:fit_screen_menu={fitScreen === true}
        class:splitScreenRight={splitScreen === true}
      >
        <slot name="menu" />
      </div>
    {/if}
  </div>
</div>

<style>
  .collapsible_wrapper {
    display: flex;
    width: 100%;
    height: 100%;
    max-width: 100%;
  }

  .view_wrapper {
    display: flex;
    flex: 1 1 0%;
    padding: 1rem 1rem 1rem calc(1rem + var(--left-overlay-offset, 0rem));
    gap: 1rem;
    height: 100%;
    width: auto;
    max-width: 100%;
    min-width: 0;
  }

  .bx--section_wrapper-canvas {
    height: 100%;
    min-width: 0;
    border-radius: 0.5rem;
    background-color: var(--pax-white);
    display: flex;
    padding: 1rem;
    flex-grow: 1;
    width: 70vw;
    align-items: normal;
  }

  .side_menu {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    --side-menu-content-gap: 1rem;
    width: 20vw;
  }

  .side_panel {
    position: relative;
    z-index: 35;
    display: flex;
    flex: 0 0 2rem;
    width: 2rem;
    min-width: 2rem;
    min-height: 0;
  }

  .side_panel :global(.side-panel) {
    height: 100%;
  }

  .embedded_wrapper {
    height: auto;
  }

  .embedded_wrapper .view_wrapper {
    height: auto;
    padding: 0;
  }

  .embedded_wrapper .bx--section_wrapper-canvas {
    width: 100%;
    height: auto;
    min-height: 0;
    padding: 0;
    background: transparent;
  }

  .seamless .view_wrapper {
    gap: 0;
    padding: 0;
    min-height: calc(100vh - 4rem);
  }

  .seamless {
    background-color: var(--pax-white);
  }

  .seamless.seamless_with_menu :global(.menu_wrapper) {
    padding-right: 1rem;
    margin-right: 1rem;
    border-right: 1px solid var(--border-color);
  }

  .seamless.seamless_with_menu :global(.menu_border) {
    border-right: 0;
  }

  .seamless.seamless_with_menu .side_menu {
    padding-left: 0;
    margin-left: 0;
    gap: 0;
    border-left: 1px solid var(--border-color);
  }

  .seamless .bx--section_wrapper-canvas {
    border-radius: 0;
    padding: 0;
    min-height: 100%;
  }

  .empty_state .bx--section_wrapper-canvas {
    align-items: center;
    justify-content: center;
  }

  .empty_state_content {
    align-items: center;
    display: flex;
    gap: 1rem;
    height: 100%;
    justify-content: center;
    width: 100%;
  }

  /* Info blue like InfoBlock type "info": an empty state is no error */
  .empty_state_icon {
    align-items: center;
    background: var(--pax-blue, #abcfe8);
    border-radius: 50%;
    display: flex;
    /* As high as the two text lines next to it, so both share one height */
    flex: 0 0 2.25rem;
    height: 2.25rem;
    justify-content: center;
    width: 2.25rem;
  }

  .empty_state_icon img {
    display: block;
    filter: brightness(0);
    height: 1.25rem;
    line-height: 0;
    width: 1.25rem;
  }

  .empty_state_text {
    text-align: left;
  }

  .seamless .side_menu {
    gap: 0;
    --side-menu-content-gap: 0;
  }

  .seamless.seamless_padding .bx--section_wrapper-canvas {
    padding: 1rem;
  }

  .seamless .fit_screen {
    height: calc(100vh - 4rem);
    max-height: calc(100vh - 4rem);
  }

  .fit_screen {
    height: calc(100vh - 6rem);
    max-height: calc(100vh - 6rem);
  }

  .fit_content {
    height: fit-content;
    min-height: calc(100vh - 9rem);
  }

  .loading_canvas {
    height: calc(100vh - 9rem) !important;
  }

  .shrink_content {
    height: fit-content;
    min-height: 0;
  }

  .fit_screen_menu {
    width: calc(25vw - 2rem);
  }

  .splitScreenLeft {
    width: 60% !important;
  }

  .splitScreenRight {
    width: 30vw;
    flex-grow: 1;
  }
</style>
